import { Badge, type BadgeTone } from "@shared/components/Badge";
import { List, ListItem } from "@shared/components/Data/List";
import { Text } from "@solomon-labs/design/components/ui";
import React from "react";

import { MOCK_VENUES, type Venue } from "./mocks/venueMocks";
import { venueIconFor } from "./venueIcon";

/** Solomon's base rate, folded into every row's combined figure.
 *  Draft: hardcoded so the page stands alone — the live page reads this
 *  from the same APY source the hero uses. */
const BASE_APY = 5.0;

/**
 * One categorical graph-palette tone per Surface Category, so the kind
 * of product is legible before the row is read. Keyed on the same
 * strings `SurfaceCategory.name` will supply; anything unmapped falls
 * back to the neutral chip rather than borrowing another category's
 * colour.
 */
const CATEGORY_TONE: Record<string, BadgeTone> = {
    "Liquidity Pool": "secondary",
    Lending: "primary",
    Treasury: "nonary",
    Neobank: "senary"
};

/**
 * `/venues` — the coverage registry.
 *
 * DRAFT. Runs entirely on `MOCK_VENUES`; see that file for the backend
 * work this needs.
 *
 * A flat `List` of providers, nothing else on the page. Product type
 * (Liquidity Pool / Lending / Treasury / Neobank) carries the liquidity
 * and risk shape on its own, so there is no tradeoff grouping and no
 * per-row prose.
 *
 * Rows are alphabetical by provider, NOT by rate: these venues carry
 * loss modes the base does not.
 */
export function VenuesPage(): React.ReactElement {
    return (
        <main className="flex-1 flex flex-col items-center px-4 py-6 md:px-6 md:py-10">
            <div className="w-full max-w-5xl flex flex-col">
                <Text variant="h1" className="text-foreground-base mb-6">
                    Venues
                </Text>

                <List className="flex flex-col">
                    {MOCK_VENUES.map((venue) => (
                        <VenueRow key={venue.slug} venue={venue} />
                    ))}
                </List>
            </div>
        </main>
    );
}

/**
 * One venue row. The trailing side is a single line: the combined rate,
 * then the venue's contribution beside it.
 *
 * The delta sits in a fixed-width box so the combined figures stay in a
 * clean column — without it, a `+18.6%` row would shove its big number
 * left of the `+2.8%` rows and the column would read ragged. The box is
 * `text-left` so the slack falls on its right: right-aligning it would
 * pad the inside of the box and visually reopen the gap that `gap-1`
 * just closed.
 */
function VenueRow({ venue }: { venue: Venue }): React.ReactElement {
    const combined = BASE_APY + venue.venueApy;
    return (
        <ListItem
            itemKey={venue.slug}
            align="center"
            padding="py-3 px-2"
            leading={venueIconFor(venue.provider)}
            trailing={
                <div className="flex items-baseline gap-2">
                    <Text
                        variant="number-lg"
                        render={<span />}
                        className="text-foreground-base tabular-nums"
                    >
                        {combined.toFixed(1)}%
                    </Text>
                    <Text
                        variant="small"
                        render={<span />}
                        className="text-success-base tabular-nums w-16 text-left"
                    >
                        +{venue.venueApy.toFixed(1)}%
                    </Text>
                </div>
            }
        >
            <div className="flex flex-col gap-0.5 min-w-0">
                {/* Provider leads; the product category rides beside it
                    as a Badge. The chip is shrink-0 so a long provider
                    name truncates rather than crushing the category. */}
                <div className="flex items-center gap-2 min-w-0">
                    <Text
                        variant="body"
                        render={<span />}
                        className="text-foreground-base truncate"
                    >
                        {venue.provider}
                    </Text>
                    <span className="shrink-0">
                        <Badge
                            tone={CATEGORY_TONE[venue.productType] ?? "default"}
                        >
                            {venue.productType}
                        </Badge>
                    </span>
                </div>
                <Text
                    variant="small"
                    render={<span />}
                    className="text-foreground-muted truncate"
                >
                    {venue.surface}
                </Text>
            </div>
        </ListItem>
    );
}
